import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tire-quote-portal/artifacts/nn6a64ea_IMG_1623.png";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      
      if (response.data.success) {
        localStorage.setItem("pp_admin_token", response.data.token);
        localStorage.setItem("pp_admin_username", response.data.username);
        toast.success("Connexion réussie");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        toast.error("Identifiants incorrects");
      } else {
        toast.error("Erreur de connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Retour au site</span>
        </Link>

        <div className="bg-zinc-900/30 border border-zinc-800 p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="PP Performance" className="h-16 mx-auto mb-6" />
            <h1 className="text-3xl font-bold uppercase tracking-tight">Administration</h1>
            <p className="text-zinc-500 mt-2">Connectez-vous pour accéder au tableau de bord</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                Nom d'utilisateur
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input 
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow pl-11"
                  placeholder="admin"
                  data-testid="input-admin-username"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                Mot de passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input 
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow pl-11"
                  placeholder="••••••••"
                  data-testid="input-admin-password"
                />
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-widest py-4 btn-mechanical"
              data-testid="admin-login-btn"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/admin/setup" 
              className="text-zinc-500 hover:text-white text-sm transition-colors"
              data-testid="admin-setup-link"
            >
              Première connexion ? Créer un compte admin
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
